import React, { FormEventHandler, useState } from 'react';
import { Alert, Form, TextInputControl } from '@ver-uds/uswds-react';
import { useFormContext } from 'react-hook-form';
import MouSignatoryRadioButtons from '../../MouSignatoryRadioButtons';
import ProgramAdminAccessRadioButtons from '../../ProgramAdminAccessRadioButtons';
import EVerifyUser from './data/EVerifyUser';
import AlertConstants from '../../../constants/AlertConstants';

export type EVerifyUsersModalFormProps = {
  id: string;
  user?: EVerifyUser;
  foundMouSignatoryId: string | undefined;
  showUserRoleOptions: boolean;
  onSubmit: FormEventHandler;
};

const EVerifyUsersModalForm = ({
  id,
  user,
  foundMouSignatoryId,
  showUserRoleOptions,
  onSubmit,
}: EVerifyUsersModalFormProps): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { register, errors } = useFormContext<EVerifyUser>();

  const [programAdmin, setProgramAdmin] = useState<string | undefined>(user?.admin);
  const [mouSignatory, setMouSignatory] = useState<string | undefined>(user?.mouSignatory);

  const mouSignatoryExists =
    (foundMouSignatoryId && !user) || (foundMouSignatoryId && user && user?.id !== foundMouSignatoryId);

  return (
    <Form id={id} className="maxw-none" onSubmit={onSubmit} noValidate>
      {/* NOTE: Until updates are made to @ver-uds, this is how we'll have to do it. */}
      <div className="maxw-mobile-lg">
        <TextInputControl
          id="email"
          name="emailAddress"
          label="Email Address"
          type="email"
          required
          ref={register({
            required: {
              value: true,
              message: 'This field is required.',
            },
            pattern: {
              value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
              message: 'This field must be formatted as an email address.',
            },
          })}
          errorId={errors.emailAddress && 'emailError'}
          errorMessage={errors.emailAddress?.message}
        />
      </div>
      <div className="maxw-mobile-lg">
        <TextInputControl
          id={`${id}-last-name`}
          name="lastName"
          label="Last Name"
          required
          ref={register({
            required: {
              value: true,
              message: 'This field is required.',
            },
            maxLength: {
              value: 40,
              message: 'This field must not exceed 40 characters.',
            },
            pattern: {
              value: /^[a-zA-Z]+(([' -][a-zA-Z ])?[a-zA-Z]*)*$/,
              message: 'This field must not contain numbers or special characters.',
            },
          })}
          errorId={errors.lastName && 'lastNameError'}
          errorMessage={errors.lastName?.message}
        />
      </div>
      <div className="maxw-mobile-lg">
        <TextInputControl
          id="firstName"
          name="firstName"
          label="First Name"
          required
          ref={register({
            required: {
              value: true,
              message: 'This field is required.',
            },
            maxLength: {
              value: 25,
              message: 'This field must not exceed 25 characters.',
            },
            pattern: {
              value: /^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$/,
              message: 'This field must not contain numbers or special characters.',
            },
          })}
          errorId={errors.firstName && 'firstNameError'}
          errorMessage={errors.firstName?.message}
        />
      </div>
      <TextInputControl
        id="middleInitial"
        name="middleInitial"
        label="Middle Initial"
        variant="small"
        ref={register({
          maxLength: {
            value: 1,
            message: 'This field must not exceed 1 character.',
          },
          pattern: {
            value: /^[a-zA-Z]+$/,
            message: 'This field must not contain numbers or special characters.',
          },
        })}
        errorId={errors.middleInitial && 'middleInitialError'}
        errorMessage={errors.middleInitial?.message}
      />
      <TextInputControl
        id="phoneNumber"
        name="phoneNumber"
        label="Phone Number"
        variant="medium"
        type="tel"
        required
        hint="10 digits, numbers only"
        ref={register({
          required: {
            value: true,
            message: 'This field is required.',
          },
          pattern: {
            value: /^\d{10}$/,
            message: 'This field must contain 10 digits.',
          },
          validate: (value: string) => {
            if (!/^(?!0|1)/.exec(value)) {
              return 'Please enter valid phone number without 0 or 1 as the first digit';
            }
            return true;
          },
        })}
        errorId={errors.phoneNumber && 'phoneNumberError'}
        errorMessage={errors.phoneNumber?.message}
      />
      <TextInputControl
        id="phoneNumberExtension"
        name="phoneNumberExtension"
        label="Phone Number Extension"
        variant="small"
        hint="Up to 6 digits, numbers only"
        ref={register({
          maxLength: {
            value: 6,
            message: 'This field must not exceed 6 digits.',
          },
          pattern: {
            value: /^\d+$/,
            message: 'This field must only contain digits.',
          },
        })}
        errorId={errors.phoneNumberExtension && 'phoneNumberExtensionError'}
        errorMessage={errors.phoneNumberExtension?.message}
      />
      {showUserRoleOptions ? (
        <>
          {mouSignatory === 'Yes' && mouSignatoryExists && (
            <Alert className="margin-top-3" status="info" slim>
              <Alert.Text>{AlertConstants.EVERIFY_USERS_MOU_SIGNATORY_WARNING}</Alert.Text>
            </Alert>
          )}
          {mouSignatory === 'No' && programAdmin === 'No' && (
            <Alert className="margin-top-3" status="error" slim>
              <Alert.Text>{AlertConstants.EVERIFY_USERS_ROLES_MODAL_ERROR}</Alert.Text>
            </Alert>
          )}
          <MouSignatoryRadioButtons onChange={setMouSignatory} />
          <ProgramAdminAccessRadioButtons onChange={setProgramAdmin} />
        </>
      ) : (
        <></>
      )}
    </Form>
  );
};

export default EVerifyUsersModalForm;
